/**
 * Game core export - entry point for all game logic
 */

import { GameCore } from '@app-games/core';
import { createInitialState } from './state.js';
import { actionHandlers } from './actions.js';
import type { GameState } from './state.js';
import type { GameAction } from './actions.js';

export type { GameState, ActiveBoost, GoldenEvent, ProducerOwned } from './state.js';
export type { PrestigeUpgradeId, Prestige2UpgradeId, AchievementStatus } from './state.js';
export { createInitialState } from './state.js';
export type { GameAction } from './actions.js';
export { actionHandlers } from './actions.js';

// Re-export utilities
export { getTotalCps, getTapPower, getComboTimeout, getCritChance, getCritMultiplier, calculateGcEarned, canPrestige, calculateCoresEarned, canPrestige2, getStartingIp, getOfflineCapHours, getOfflineEfficiency, calculateOfflineIp, getProducerCost } from './actions.js';

// Data exports
export { PRODUCERS, getMilestoneMultiplier, MILESTONES, getProducerById } from './data/producers.js';
export type { ProducerDef, Milestone } from './data/producers.js';
export { CLICK_UPGRADES, SPECIAL_UPGRADES } from './data/upgrades.js';
export type { ClickUpgradeDef, SpecialUpgradeDef } from './data/upgrades.js';
export { PRESTIGE_UPGRADES, getPrestigeUpgradeCost, PRESTIGE2_UPGRADES } from './data/prestige.js';
export type { PrestigeUpgradeDef } from './data/prestige.js';
export { ALL_ACHIEVEMENTS, checkAchievements, totalProducerCount } from './data/achievements.js';
export type { AchievementDef, AchievementCategory } from './data/achievements.js';
export { RANKS, getCurrentRank, getEligibleRank, getNextPromotableRank } from './data/ranks.js';
export type { RankDef } from './data/ranks.js';
export { MISSION_TEMPLATES, generateDailyMissions, getUtcDateString, getMissionTemplate, ALL_COMPLETE_BONUS_DOCS, DAILY_MISSION_COUNT } from './data/missions.js';
export type { MissionTemplate, MissionType, MissionDifficulty, MissionReward, DailyMission } from './data/missions.js';

// Conquest exports
export { COUNTRIES, getFacilityCost } from './data/conquest.js';
export type { CountryDef, FacilityDef } from './data/conquest.js';
export { isCountryUnlocked, isAllConquered, getConqueredCount, getConquestCpsMult, getConquestTapMult, getConquestOfflineBonus, getConquestGcBonus, getConquestGoldenBonus, getConquestProducerDiscount, getInfiltrationPower } from './systems/conquest.js';
export type { ConquestCountryState, ConquestState } from './state.js';

/**
 * Create game core instance
 */
export function createGameCore(debug = false) {
  const core = new GameCore<GameState, GameAction>({
    createInitialState,
    debug,
    historySize: 100,
  });

  // Register action handlers
  for (const [type, handler] of Object.entries(actionHandlers)) {
    core.registerAction(type as GameAction['type'], handler as any);
  }

  return core;
}
